import React, { useRef, useState } from "react";
import { Filter } from "lucide-react";
import {
  FilterPanel,
  SEARCHABLE_FROM,
  isFilterActive,
  isOptionActive,
  toggleOption,
} from "./FilterPanel";
import { cn } from "../../utils/cn";

// Shared mobile chip recipe; min-h-10 keeps the touch target ≥40px.
export const chip = (active) =>
  cn(
    "inline-flex min-h-10 shrink-0 items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition-colors",
    active
      // primary-hover, not primary: primary is 4.49:1 on the /10 tint, under AA
      ? "border-primary bg-primary/10 text-primary-hover font-medium"
      : "border-border bg-background text-muted-foreground"
  );

// Too many options to lay out as chips: same dropdown the desktop column header
// opens, hung off a chip trigger instead of a filter icon.
function FilterChipDropdown({ filter, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const active = isFilterActive(filter);

  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(chip(active), "py-1")}
      >
        <Filter className="h-3 w-3" />
        {label}
        {filter.multiple && active && (
          <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] leading-none text-primary-foreground">
            {filter.value.length}
          </span>
        )}
      </button>
      {open && <FilterPanel filter={filter} label={label} anchorRef={ref} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * The `lg` breakpoint drops the table for cards, taking the column headers —
 * and their filters — with it. This renders the same filter descriptors
 * (see FilterPanel) as a stack of labelled rows: chips for a short option
 * list, the column dropdown behind a chip for a long one.
 *
 * <MobileFilterBar filters={[{ key, label, value, onChange, options, multiple }]} />
 */
export function MobileFilterBar({ filters, className }) {
  const usable = filters.filter((f) => f.options.length > 0);
  if (usable.length === 0) return null;

  return (
    <div className={cn("space-y-3", className)}>
      {usable.map((filter) => (
        <div key={filter.key || filter.label} className="flex items-center gap-2">
          {filter.options.length > SEARCHABLE_FROM ? (
            // The chip trigger carries the label itself, so no separate one.
            <FilterChipDropdown filter={filter} label={filter.label} />
          ) : (
            <>
              <span className="text-xs text-muted-foreground shrink-0">{filter.label}</span>
              {/* Scrolls sideways like the Sort row above it. Wrapping stacked a long option
                  list onto extra rows that ran into the next filter. */}
              <div className="flex items-center gap-1 overflow-x-auto py-0.5">
                {filter.options.map((opt) => (
                  <button
                    key={opt.value || "__all__"}
                    type="button"
                    onClick={() =>
                      filter.multiple
                        ? toggleOption(filter, opt.value)
                        : filter.onChange(opt.value)
                    }
                    className={chip(isOptionActive(filter, opt.value))}
                  >
                    {opt.label}
                  </button>
                ))}
                {filter.multiple && isFilterActive(filter) && (
                  <button
                    type="button"
                    onClick={() => filter.onChange([])}
                    className={cn(chip(false), "hover:bg-muted")}
                  >
                    {filter.clearLabel || "Clear"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}
